// Iter 90i — Shared toolbar for list/index pages.
//
// Renders a consistent "N results · Columns · CSV · PDF" strip identical
// to the one on Partners.jsx. Any list page can drop this in instead of
// hand-rolling the Columns popover + export buttons.
//
// Props:
//   count           — number shown on the left ("N results")
//   label           — noun for the count, default "result" (auto-pluralises)
//   loading         — shows "…" instead of the number
//   columns         — [{ id, label, always? }]  full catalog of possible columns
//   visibleIds      — currently visible column order (excluding "actions")
//   onToggle(id)    — user checked / unchecked a column
//   onMove(id, dir) — dir = -1 up | +1 down
//   onReset         — restore defaults
//   onExportCsv, onExportPdf
//   exporting       — disables both export buttons while a job runs
//   testidPrefix    — unique per page (e.g. "customers", "did-master")
import {
  Columns3, ChevronUp, ChevronDown, Download, FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";

export function ColumnsToolbar({
  count, label = "result", loading = false,
  columns, visibleIds, onToggle, onMove, onReset,
  onExportCsv, onExportCsvWithUsers, onExportPdf, exporting = false,
  extraLeft = null, extraRight = null, testidPrefix,
}) {
  const pluralised = `${count} ${label}${count === 1 ? "" : "s"}`;
  const movable = visibleIds.filter((id) => id !== "actions");
  return (
    <div className="ml-auto flex items-end gap-1.5 self-end">
      {extraLeft}
      <span className="mr-1 text-xs text-slate-400" data-testid={`${testidPrefix}-result-count`}>
        {loading ? "…" : pluralised}
      </span>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm"
                  data-testid={`${testidPrefix}-columns-btn`} className="h-9">
            <Columns3 className="h-3.5 w-3.5" /> Columns
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-2" align="end"
                        data-testid={`${testidPrefix}-columns-popover`}>
          <div className="mb-1 px-2 pt-1 text-[11px] font-medium uppercase tracking-wide text-slate-400">
            Show &amp; reorder columns
          </div>
          <div className="space-y-0.5">
            {movable.map((id, idx) => {
              const col = columns.find((c) => c.id === id);
              if (!col) return null;
              // Skip pinned columns before the first movable slot — they
              // can't be reordered above/below. For our current pages this
              // is always "name"/"partner" at index 0.
              const firstMovableIdx = movable.findIndex((mid) => {
                const mc = columns.find((c) => c.id === mid);
                return mc && !mc.always;
              });
              const lastMovableIdx = (() => {
                for (let i = movable.length - 1; i >= 0; i -= 1) {
                  const mc = columns.find((c) => c.id === movable[i]);
                  if (mc && !mc.always) return i;
                }
                return -1;
              })();
              const first = idx <= firstMovableIdx;
              const last  = idx >= lastMovableIdx;
              return (
                <div key={id}
                     data-testid={`${testidPrefix}-col-picker-${id}`}
                     className="flex items-center gap-2 rounded px-2 py-1 hover:bg-slate-50">
                  <Checkbox checked disabled={col.always}
                            onCheckedChange={() => onToggle(id)}
                            data-testid={`${testidPrefix}-col-toggle-${id}`} />
                  <span className={`flex-1 text-sm ${col.always ? "text-slate-400" : "text-slate-700"}`}>
                    {col.label || id}
                    {col.always && <span className="ml-1 text-[10px] text-slate-300">(pinned)</span>}
                  </span>
                  {!col.always && (
                    <>
                      <button onClick={() => onMove(id, -1)} disabled={first}
                              data-testid={`${testidPrefix}-col-up-${id}`}
                              className="rounded p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-30">
                        <ChevronUp className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => onMove(id, 1)} disabled={last}
                              data-testid={`${testidPrefix}-col-down-${id}`}
                              className="rounded p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-30">
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </div>
              );
            })}
            {columns.filter((c) => !c.always && !visibleIds.includes(c.id)).map((c) => (
              <div key={c.id}
                   data-testid={`${testidPrefix}-col-picker-${c.id}`}
                   className="flex items-center gap-2 rounded px-2 py-1 opacity-70 hover:bg-slate-50">
                <Checkbox checked={false}
                          onCheckedChange={() => onToggle(c.id)}
                          data-testid={`${testidPrefix}-col-toggle-${c.id}`} />
                <span className="flex-1 text-sm text-slate-500">{c.label || c.id}</span>
              </div>
            ))}
          </div>
          {onReset && (
            <div className="mt-2 border-t border-slate-100 pt-1.5 text-center">
              <button data-testid={`${testidPrefix}-col-reset`}
                      onClick={onReset}
                      className="text-[11px] font-medium text-slate-400 hover:text-slate-700">
                Reset to defaults
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>
      {onExportCsv && (
        <Button variant="outline" size="sm"
                disabled={exporting || count === 0}
                onClick={onExportCsv}
                data-testid={`${testidPrefix}-export-csv`} className="h-9">
          <Download className="h-3.5 w-3.5" /> CSV
        </Button>
      )}
      {onExportCsvWithUsers && (
        <Button variant="outline" size="sm"
                disabled={exporting || count === 0}
                onClick={onExportCsvWithUsers}
                title="One row per user, with the partner's details on each row"
                data-testid={`${testidPrefix}-export-csv-users`} className="h-9">
          <Download className="h-3.5 w-3.5" /> CSV + users
        </Button>
      )}
      {onExportPdf && (
        <Button variant="outline" size="sm"
                disabled={exporting || count === 0}
                onClick={onExportPdf}
                data-testid={`${testidPrefix}-export-pdf`} className="h-9">
          <FileText className="h-3.5 w-3.5" /> PDF
        </Button>
      )}
      {extraRight}
    </div>
  );
}
